'use client';

import {
	Area,
	CartesianGrid,
	ComposedChart,
	Legend,
	Line,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const SOLAR_PROFILE = [
	0, 0, 0, 0, 0, 0.01, 0.04, 0.12, 0.28, 0.52, 0.75, 0.92, 1.0, 0.98, 0.88, 0.68, 0.4, 0.18, 0.05,
	0.01, 0, 0, 0, 0,
];

function generateCurve(kwp: number, avgConsKw: number) {
	return HOURS.map((h, i) => {
		const hourStr = `${String(h).padStart(2, '0')}:00`;
		const prodBase = SOLAR_PROFILE[h] * kwp;
		const consBase = avgConsKw + Math.sin((h - 6) * 0.4) * (avgConsKw * 0.5);
		const pseudoRand = Math.abs(Math.sin(i)) * (avgConsKw * 0.2);

		return {
			hour: hourStr,
			produzione: Math.round(prodBase * 10) / 10,
			consumo: Math.round((consBase + pseudoRand) * 10) / 10,
		};
	});
}

export interface ProductionConsumptionPoint {
	hour: string;
	produzione: number;
	consumo: number;
}

export interface ProductionConsumptionChartProps {
	kwp?: number;
	cons?: number;
	data?: ProductionConsumptionPoint[];
	unit?: 'kW' | 'kWh';
	emptyMessage?: string;
	variant?: 'area' | 'line';
	xAxisInterval?: number;
}

export function ProductionConsumptionChart({
	kwp = 0,
	cons = 0,
	data: measuredData,
	unit = 'kW',
	emptyMessage = 'Nessun dato - collega i misuratori GSE o configura l’impianto',
	variant = 'area',
	xAxisInterval = 3,
}: ProductionConsumptionChartProps) {
	const avgConsKw = cons > 0 ? cons / 8760 : 0;
	const data = measuredData ?? generateCurve(kwp, avgConsKw);
	const isEmpty =
		measuredData !== undefined
			? data.length === 0 || data.every((point) => point.produzione === 0 && point.consumo === 0)
			: kwp === 0 && cons === 0;

	return (
		<div className="relative">
			<ResponsiveContainer width="100%" height={180}>
				<ComposedChart data={data}>
					<defs>
						<linearGradient id="gProd" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="var(--color-accent)" stopOpacity={0.4} />
							<stop offset="100%" stopColor="var(--color-accent)" stopOpacity={0} />
						</linearGradient>
						<linearGradient id="gCons" x1="0" y1="0" x2="0" y2="1">
							<stop offset="0%" stopColor="var(--color-accent-purple)" stopOpacity={0.3} />
							<stop offset="100%" stopColor="var(--color-accent-purple)" stopOpacity={0} />
						</linearGradient>
					</defs>
					<CartesianGrid stroke="#1a3a5c" strokeDasharray="3 3" vertical={false} />
					<XAxis
						dataKey="hour"
						tick={{ fontSize: 11, fill: '#64748b' }}
						tickLine={false}
						axisLine={false}
						interval={xAxisInterval}
					/>
					<YAxis
						tick={{ fontSize: 11, fill: '#64748b' }}
						tickLine={false}
						axisLine={false}
						unit={` ${unit}`}
						width={unit === 'kWh' ? 55 : 45}
					/>
					<Tooltip
						contentStyle={{
							background: '#0f1f35',
							border: '1px solid #1a3a5c',
							borderRadius: 8,
							fontSize: 13,
						}}
						labelStyle={{ color: '#e2e8f0' }}
					/>
					<Legend iconSize={8} wrapperStyle={{ fontSize: 12 }} />
					{variant === 'line' ? (
						<>
							<Line
								type="monotone"
								dataKey="produzione"
								name="Produzione"
								unit={` ${unit}`}
								stroke="var(--color-accent)"
								strokeWidth={2}
								dot={data.length === 1 ? { r: 3 } : false}
							/>
							<Line
								type="monotone"
								dataKey="consumo"
								name="Consumo"
								unit={` ${unit}`}
								stroke="var(--color-accent-purple)"
								strokeWidth={1.5}
								dot={data.length === 1 ? { r: 3 } : false}
							/>
						</>
					) : (
						<>
							<Area
								type="monotone"
								dataKey="produzione"
								name="Produzione"
								unit={` ${unit}`}
								stroke="var(--color-accent)"
								fill="url(#gProd)"
								strokeWidth={2}
							/>
							<Area
								type="monotone"
								dataKey="consumo"
								name="Consumo"
								unit={` ${unit}`}
								stroke="var(--color-accent-purple)"
								fill="url(#gCons)"
								strokeWidth={1.5}
							/>
						</>
					)}
				</ComposedChart>
			</ResponsiveContainer>
			{isEmpty && (
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
					<span className="rounded-md bg-bg-card/80 px-3 py-1.5 text-[13px] text-text-muted backdrop-blur-sm">
						{emptyMessage}
					</span>
				</div>
			)}
		</div>
	);
}
